import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllSubCategories,
  clearAllSubCategories,
} from "./../../actions/subcategoryActions";
import { Link } from "react-router-dom";
import {
  Box,
  InputBase,
  Typography,
  Paper,
  Toolbar,
  AppBar,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Add, Search, Close, Edit, Videocam, Audiotrack } from "@mui/icons-material";
import MainContentContainer from "../MainContentContainer/MainContentContainer";

const SubCategoriesTable = ({ selectedId }) => {
  const dispatch = useDispatch();
  const rawSubcategories = useSelector((state) => state.subcategories.subcategories);
  const subcategories = useMemo(
    () => rawSubcategories ? Object.values(rawSubcategories) : null,
    [rawSubcategories]
  );
  const [searchText, setSearchText] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    dispatch(clearAllSubCategories());
    dispatch(fetchAllSubCategories());
  }, [dispatch]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+F (Mac) or Ctrl+F (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && e.shiftKey) {
        e.preventDefault();
        if (!isSearchOpen) {
          handleToggleSearch();
        }
      }
      // Close search with Escape
      if (e.key === 'Escape' && isSearchOpen) {
        e.preventDefault();
        handleToggleSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleToggleSearch = () => {
    setIsSearchOpen((prev) => !prev);
    if (isSearchOpen) {
      setSearchText("");
    } else {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Escape') {
      setIsSearchOpen(false);
      setSearchText("");
    }
  };

  const renderSubcategories = () => {
    if (!subcategories) return null;

    let data = [...subcategories];
    data = data.filter((el) => !el.subcategory_is_book);

    if (searchText !== "") {
      const search = searchText.toLowerCase();
      data = data.filter(
        (obj) => obj.subcategory_title.toLowerCase().indexOf(search) > -1
      );
    }

    // Split data into Audio and Quick Tap sections and sort by active status first, then by category name
    const audioSubcategories = data
      .filter(obj => !obj.subcategory_is_quick_tap)
      .sort((a, b) => {
        // First sort by active status (active = 1, inactive = 0) - active first
        if (a.subcategory_is_active !== b.subcategory_is_active) {
          return b.subcategory_is_active - a.subcategory_is_active;
        }

        // Then sort by category name
        const categoryA = a.category_title || "N/A";
        const categoryB = b.category_title || "N/A";
        // If either category is N/A, it should go to the bottom
        if (categoryA === "N/A" && categoryB !== "N/A") return 1;
        if (categoryA !== "N/A" && categoryB === "N/A") return -1;
        // Otherwise sort alphabetically
        return categoryA.toLowerCase().localeCompare(categoryB.toLowerCase());
      });

    const quickTapSubcategories = data
      .filter(obj => obj.subcategory_is_quick_tap)
      .sort((a, b) => {
        // First sort by active status (active = 1, inactive = 0) - active first
        if (a.subcategory_is_active !== b.subcategory_is_active) {
          return b.subcategory_is_active - a.subcategory_is_active;
        }

        // Then sort by category name
        const categoryA = a.category_title || "N/A";
        const categoryB = b.category_title || "N/A";
        // If either category is N/A, it should go to the bottom
        if (categoryA === "N/A" && categoryB !== "N/A") return 1;
        if (categoryA !== "N/A" && categoryB === "N/A") return -1;
        // Otherwise sort alphabetically
        return categoryA.toLowerCase().localeCompare(categoryB.toLowerCase());
      });

    const renderSection = (items, title, isQuickTap) => (
      <>
        <TableRow>
          <TableCell colSpan={5} sx={{ backgroundColor: '#f0f0f0', py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isQuickTap ? <Videocam /> : <Audiotrack />}
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {title}
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
          <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Title</strong></TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Category</strong></TableCell>
          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}><strong>Status</strong></TableCell>
          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}><strong>Sessions</strong></TableCell>
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Actions</strong></TableCell>
        </TableRow>
        {items.map((obj) => (
          <TableRow key={obj.subcategory_id + obj.subcategory_title}>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={obj.subcategory_is_quick_tap ? 'Quick Tap' : 'Audio'}>
                  <IconButton>
                    {obj.subcategory_is_quick_tap ? (
                      <Videocam />
                    ) : (
                      <Audiotrack />
                    )}
                  </IconButton>
                </Tooltip>
                <Typography variant="subtitle1">{obj.subcategory_title}</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">{obj.category_title || "N/A"}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography
                sx={{
                  color: obj.subcategory_is_active ? 'green' : 'red',
                  fontWeight: 500,
                }}
              >
                {obj.subcategory_is_active ? 'Active' : 'Inactive'}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="subtitle1">
                {obj.subcategory_num_sessions || 0}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="Edit" placement="top">
                <IconButton
                  component={Link}
                  to={`/subcategories/edit/${obj.subcategory_id}`}
                  color="info"
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </>
    );

    return (
      <>
        {renderSection(audioSubcategories, 'Audio Subcategories', false)}
        {renderSection(quickTapSubcategories, 'Quick Tap Subcategories', true)}
      </>
    );
  };

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          {isSearchOpen ? (
            <Box display="flex" alignItems="center" flexGrow={1}>
              <InputBase
                inputRef={searchInputRef}
                value={searchText}
                onChange={handleSearchChange}
                onKeyDown={handleKeyPress}
                sx={{ flexGrow: 1, px: 1, border: "1px solid #ccc", borderRadius: 1, backgroundColor: "white" }}
                placeholder="Search subcategories... (Press Esc to close)"
              />
              <IconButton color="inherit" onClick={handleToggleSearch}>
                <Close />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Subcategories
            </Typography>
          )}
          <IconButton color="inherit" onClick={handleToggleSearch}>
            <Search />
          </IconButton>
          <Button color="inherit" component={Link} to="/subcategories/add" startIcon={<Add />}>
            Add New
          </Button>
        </Toolbar>
      </AppBar>

      <TableContainer component={Paper} sx={{ marginTop: 3 }}>
        <Table aria-label="subcategories table">
          <TableBody>
            {renderSubcategories()}
          </TableBody>
        </Table>
      </TableContainer>
    </MainContentContainer>
  );
};

export default SubCategoriesTable;
